import { PropsWithChildren } from 'react';

import { ActionCallback, ActionContext } from 'types/context';

import { ActionsContext } from './actions.context';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const callbacks = new Map<string, ((...args: any[]) => void)[]>();

function add<TArgs = unknown>(name: string, callback: ActionCallback<TArgs>, preventDuplicateCallbacks = false) {
  if (callbacks.has(name)) {
    const current = callbacks.get(name);

    if (
      preventDuplicateCallbacks &&
      current?.some((cb) => {
        return cb.toString() === callback.toString();
      })
    ) {
      return;
    }

    callbacks.set(name, [...(current ?? []), callback]);

    return;
  }

  callbacks.set(name, [callback]);
}

function cleanup(...names: string[]) {
  for (const name of names) {
    const keys = Array.from(callbacks.keys());

    for (const key of keys) {
      const regex = new RegExp(`^${name}$`);

      if (regex.test(key)) {
        callbacks.delete(key);
      }
    }
  }
}

function dispatch<TArgs = unknown>(name: string, ...args: TArgs[]) {
  if (callbacks.has(name)) {
    const actions = callbacks.get(name) ?? [];

    for (const action of actions) {
      action?.(...args);
    }
  }
}

function remove<TArgs = unknown>(name: string, callback: ActionCallback<TArgs>) {
  if (callbacks.has(name)) {
    const current = callbacks.get(name) ?? [];
    const index = current.findIndex((cb) => cb.toString() === callback.toString());

    if (index >= 0) {
      callbacks.set(name, [...current.slice(0, index), ...current.slice(index + 1)]);
    }
  }
}

function ActionsProvider({ children }: PropsWithChildren) {
  const value: ActionContext = {
    add,
    cleanup,
    dispatch,
    remove,
  };

  return <ActionsContext.Provider value={value}>{children}</ActionsContext.Provider>;
}

export default ActionsProvider;
